import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain, EventMetadata, NOT_SPECIFIED} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_STUDIES_EVENTS_VERSION = 6;

export enum ProfilerStudiesFeatures {
  GENERATE_ANALYSIS = 'generateAnalysis',
  CREATE_STUDY = 'createStudy',
  CREATE_EXPERIMENT = 'createExperiment',
  ASSIGN_UNIT_OPERATION = 'assignUnitOperation',
  PROCEED_TO_EXECUTION = 'proceedToExecution',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerStudiesFeatures]: Function} = {
  [ProfilerStudiesFeatures.GENERATE_ANALYSIS]: processStudiesGenerateAnalysis,
  [ProfilerStudiesFeatures.CREATE_STUDY]: processStudiesCreateStudy,
  [ProfilerStudiesFeatures.CREATE_EXPERIMENT]: processStudiesCreateExperiment,
  [ProfilerStudiesFeatures.ASSIGN_UNIT_OPERATION]: processStudiesAssignUnitOperation,
  [ProfilerStudiesFeatures.PROCEED_TO_EXECUTION]: processStudiesProceedToExecution,
};

enum ProfilerStudiesActions {
  STUDIES_GENERATE_ANALYSIS_STARTED = 'studiesGenerateAnalysisStarted',
  STUDIES_GENERATE_ANALYSIS_ENDED = 'studiesGenerateAnalysisEnded',
  STUDIES_CREATE_STUDY_STARTED = 'studiesCreateStudyStarted',
  STUDIES_CREATE_STUDY_ENDED = 'studiesCreateStudyEnded',
  STUDIES_CREATE_EXPERIMENT_STARTED = 'studiesCreateExperimentStarted',
  STUDIES_CREATE_EXPERIMENT_ENDED = 'studiesCreateExperimentEnded',
  STUDIES_ASSIGN_UNIT_OPERATION_STARTED = 'studiesAssignUnitOperationStarted',
  STUDIES_ASSIGN_UNIT_OPERATION_ENDED = 'studiesAssignUnitOperationEnded',
  STUDIES_PROCEED_TO_EXECUTION_STARTED = 'studiesProceedToExecutionStarted',
  STUDIES_PROCEED_TO_EXECUTION_ENDED = 'studiesProceedToExecutionEnded',
}

export const STUDIES_GENERATE_ANALYSIS_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.GENERATE_ANALYSIS,
  action: ProfilerStudiesActions.STUDIES_GENERATE_ANALYSIS_STARTED,
};
export const STUDIES_GENERATE_ANALYSIS_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.GENERATE_ANALYSIS,
  action: ProfilerStudiesActions.STUDIES_GENERATE_ANALYSIS_ENDED,
};
export const STUDIES_CREATE_STUDY_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.CREATE_STUDY,
  action: ProfilerStudiesActions.STUDIES_CREATE_STUDY_STARTED,
};
export const STUDIES_CREATE_STUDY_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.CREATE_STUDY,
  action: ProfilerStudiesActions.STUDIES_CREATE_STUDY_ENDED,
};

export const STUDIES_CREATE_EXPERIMENT_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.CREATE_EXPERIMENT,
  action: ProfilerStudiesActions.STUDIES_CREATE_EXPERIMENT_STARTED,
};

export const STUDIES_CREATE_EXPERIMENT_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.CREATE_EXPERIMENT,
  action: ProfilerStudiesActions.STUDIES_CREATE_EXPERIMENT_ENDED,
};

export const STUDIES_ASSIGN_UNIT_OPERATION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.ASSIGN_UNIT_OPERATION,
  action: ProfilerStudiesActions.STUDIES_ASSIGN_UNIT_OPERATION_STARTED,
};

export const STUDIES_ASSIGN_UNIT_OPERATION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.ASSIGN_UNIT_OPERATION,
  action: ProfilerStudiesActions.STUDIES_ASSIGN_UNIT_OPERATION_ENDED,
};

export const STUDIES_PROCEED_TO_EXECUTION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.PROCEED_TO_EXECUTION,
  action: ProfilerStudiesActions.STUDIES_PROCEED_TO_EXECUTION_STARTED,
};

export const STUDIES_PROCEED_TO_EXECUTION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.STUDIES,
  feature: ProfilerStudiesFeatures.PROCEED_TO_EXECUTION,
  action: ProfilerStudiesActions.STUDIES_PROCEED_TO_EXECUTION_ENDED,
};

/**
 * Build metadata for studies generate analysis event
 */
export function composeStudiesGenerateAnalysisEventMetadata(action?: string): EventMetadata {
  const actionValue = toDatadogTagFormat(action ?? NOT_SPECIFIED);
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations.
  return {
    distributionMetadata: {
      action: actionValue,
    },
    actionMetadata: {
      action: actionValue,
    },
  };
}

export enum StudyCreationExecutionType {
  SYNC = 'sync',
  ASYNC = 'async',
}

export enum ExperimentCreationExecutionType {
  SYNC = 'sync',
  ASYNC = 'async',
}

/**
 * Build metadata for studies create study event
 */
export function composeStudiesCreateStudyEventMetadata(
  studyType: StudyType,
  executionType: StudyCreationExecutionType,
  actionOrigin: ActionOrigin
): EventMetadata {
  const studyTypeValue = toDatadogTagFormat(studyType);
  const executionTypeValue = toDatadogTagFormat(executionType);
  const actionOriginValue = toDatadogTagFormat(actionOrigin);
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations.
  return {
    distributionMetadata: {
      study_type: studyTypeValue,
      execution_type: executionTypeValue,
      action: actionOriginValue,
    },
    actionMetadata: {
      study_type: studyTypeValue,
      execution_type: executionTypeValue,
      action: actionOriginValue,
    },
  };
}

/**
 * Build metadata for studies create experiment event
 */
export function composeStudiesCreateExperimentEventMetadata(
  studyType: StudyType,
  executionType: ExperimentCreationExecutionType
): EventMetadata {
  const studyTypeValue = toDatadogTagFormat(studyType);
  const executionTypeValue = toDatadogTagFormat(executionType);
  return {
    distributionMetadata: {
      study_type: studyTypeValue,
      execution_type: executionTypeValue,
    },
    actionMetadata: {
      study_type: studyTypeValue,
      execution_type: executionTypeValue,
    },
  };
}

/**
 * Composes the event metadata for the studies assign unit operation.
 *
 * @param actionOrigin - The origin of the action.
 * @returns The event metadata object.
 */
export function composeStudiesAssignUnitOperationEventMetadata(actionOrigin: ActionOrigin): EventMetadata {
  const actionOriginValue = toDatadogTagFormat(actionOrigin);
  return {
    distributionMetadata: {
      action: actionOriginValue,
    },
    actionMetadata: {
      action: actionOriginValue,
    },
  };
}

/**
 * Composes the event metadata for the studies proceed to execution.
 *
 * @param studyType - The type of the study.
 * @param actionOrigin - The origin of the action.
 * @returns The event metadata object.
 */
export function composeStudiesProceedToExecutionEventMetadata(
  studyType: StudyType,
  actionOrigin: ActionOrigin
): EventMetadata {
  const studyTypeValue = toDatadogTagFormat(studyType);
  const actionOriginValue = toDatadogTagFormat(actionOrigin);
  return {
    distributionMetadata: {
      study_type: studyTypeValue,
      action: actionOriginValue,
    },
    actionMetadata: {
      study_type: studyTypeValue,
      action: actionOriginValue,
    },
  };
}

export function processStudiesEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerStudiesFeatures;
  assert(
    Object.values(ProfilerStudiesFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

/**
 * Process a studies generate analysis event
 */
function processStudiesGenerateAnalysis(): void {
  processTimingEvent(
    STUDIES_GENERATE_ANALYSIS_STARTED_EVENT,
    STUDIES_GENERATE_ANALYSIS_ENDED_EVENT,
    'Studies Generate Analysis',
    'perf.studies_generate_analysis',
    PROFILER_STUDIES_EVENTS_VERSION
  );
}

/**
 * Process a studies create study event
 */
function processStudiesCreateStudy(): void {
  processTimingEvent(
    STUDIES_CREATE_STUDY_STARTED_EVENT,
    STUDIES_CREATE_STUDY_ENDED_EVENT,
    'Studies Create Study',
    'perf.studies_create_study',
    PROFILER_STUDIES_EVENTS_VERSION
  );
}

/**
 * Process a studies create experiment event
 */
function processStudiesCreateExperiment(): void {
  processTimingEvent(
    STUDIES_CREATE_EXPERIMENT_STARTED_EVENT,
    STUDIES_CREATE_EXPERIMENT_ENDED_EVENT,
    'Studies Create Experiment',
    'perf.studies_create_experiment',
    PROFILER_STUDIES_EVENTS_VERSION
  );
}

/**
 * Process a studies assign unit operation event
 */
function processStudiesAssignUnitOperation(): void {
  processTimingEvent(
    STUDIES_ASSIGN_UNIT_OPERATION_STARTED_EVENT,
    STUDIES_ASSIGN_UNIT_OPERATION_ENDED_EVENT,
    'Studies Assign Unit Operation',
    'perf.studies_assign_unit_operation',
    PROFILER_STUDIES_EVENTS_VERSION
  );
}

/**
 * Process a studies proceed to execution event
 */
function processStudiesProceedToExecution(): void {
  processTimingEvent(
    STUDIES_PROCEED_TO_EXECUTION_STARTED_EVENT,
    STUDIES_PROCEED_TO_EXECUTION_ENDED_EVENT,
    'Studies Proceed to Execution',
    'perf.studies_proceed_to_execution',
    PROFILER_STUDIES_EVENTS_VERSION
  );
}
